/// <reference types="vite/client" />

import { getAppConfig } from '@idu-nightlife/nightlife-utils';
import { z } from 'zod';

// Deploy-time `/config.js` first, then this build's env — one bundle per build, not one per
// environment. See specs/029-feature-branch-deployment/contracts/runtime-config.md
const appConfig = getAppConfig({ prefix: 'MAN_VITE_', env: import.meta.env });

const zNightlifeInstances = z.array(
  z.object({
    key: z.string().regex(/^[a-z0-9]+$/),
    portalUrl: z.url(),
    apiUrl: z.url(),
  }),
);

export type NightlifeInstance = z.infer<typeof zNightlifeInstances>[number];

/**
 * The nightlife projects this deployment embeds, e.g. `[{"key":"osnp","portalUrl":…,"apiUrl":…}]`:
 * each one's manager portal and the Express API that lists its venues. Unset or invalid → none.
 */
export function parseNightlifeInstances(raw: string | undefined): NightlifeInstance[] {
  if (!raw) return [];

  try {
    return zNightlifeInstances.parse(JSON.parse(raw));
  } catch (error) {
    console.error('[config] MAN_VITE_NIGHTLIFE_INSTANCES is not a valid instance list', error);
    return [];
  }
}

export const CONFIG = {
  APP_NAME: 'IDU Manager',
  API_BASE_URL: appConfig.apiBaseUrl,
  FIREBASE: appConfig.firebase,

  // Preview channels resolve their sibling URLs at deploy time (spec 029 D9); the
  // per-environment env values below stay the fallback everywhere else.
  SIBLING_APP_ORIGINS: appConfig.siblingAppOrigins,
  CHECKD_URL: import.meta.env.MAN_VITE_CHECKD_URL,
  IDU_DASHBOARD_URL: import.meta.env.MAN_VITE_IDU_DASHBOARD_URL,
  // Per-environment flags: the deploy writes them into config.js; locally MAN_VITE_* supplies them.
  NIGHTLIFE_INSTANCES: parseNightlifeInstances(appConfig.flags?.NIGHTLIFE_INSTANCES),
  NIGHTLIFE_DASHBOARD_URL: appConfig.flags?.NIGHTLIFE_DASHBOARD_URL,

  BUILD_ID: appConfig.buildId,
  ENVIRONMENT: appConfig.environment,
};
